// ─────────────────────────────────────────────────────────────────────────────
// GENERATING A DOCUMENT, IN THE BROWSER
//
// The engine existed as a Python script in the repository, which is useful to
// me and useless to Core — a browser cannot run Python, so the Generate
// document screen went on saying "not built yet", correctly.
//
// This is the same logic in JavaScript. A .docx is a zip of XML: unzip,
// substitute the {{FIELDS}}, rezip, hand the browser a download. No server, no
// upload, nothing leaves the machine — which for a letter naming a client is
// worth having by accident as much as by design.
//
// TWO THINGS THAT ARE NOT A SIMPLE REPLACE, both learned the hard way in the
// Python version:
//
//   A multi-line address cannot be a newline in XML — Word ignores it and the
//   whole address prints as one run-on line. Each line becomes its own <w:br/>.
//
//   Word splits text across runs for its own reasons, so {{REFERENCE}} as you
//   see it on screen may be three separate elements in the file and a plain
//   replace finds nothing. The runs are merged first. Without that the letter
//   generates, looks right at a glance, and has {{REFERENCE}} printed on it.
// ─────────────────────────────────────────────────────────────────────────────

import JSZip from "jszip";

const FIELD = /\{\{([A-Z_]+)\}\}/g;

function mergeRuns(xml) {
  // Collapse adjacent runs so a field typed in one go is one string again.
  let prev = null;
  while (prev !== xml) {
    prev = xml;
    xml = xml.replace(/<\/w:t><\/w:r>(<w:r>(?:<w:rPr>[\s\S]*?<\/w:rPr>)?)<w:t[^>]*>/, "");
  }
  return xml;
}

function escapeXml(text) {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function asXml(value) {
  if (value === null || value === undefined) return "";
  const lines = String(value).split("\n");
  if (lines.length === 1) return escapeXml(lines[0]);
  // A break between lines, not a newline: Word renders a newline as nothing.
  return lines.map(escapeXml).join('</w:t><w:br/><w:t xml:space="preserve">');
}

/**
 * Fill a template and hand the browser a download.
 * Returns the names of any fields left unfilled, so the caller can say so
 * rather than producing a letter with {{SUBJECT}} printed on it.
 */
export async function generateDocument(templateUrl, values, filename) {
  const response = await fetch(templateUrl);
  if (!response.ok) {
    throw new Error(
      "The template could not be loaded. Templates live in /templates and are " +
      "deployed with the application."
    );
  }
  const zip = await JSZip.loadAsync(await response.arrayBuffer());

  const unfilled = new Set();

  // The header and footer carry fields too — the company name and address at
  // the top, and whatever the jurisdiction requires at the foot. Filling only
  // document.xml leaves a letter with {{COMPANY_NAME}} above the address.
  const parts = Object.keys(zip.files).filter(
    (n) => n === "word/document.xml" || /^word\/(header|footer)\d*\.xml$/.test(n)
  );

  for (const part of parts) {
    let xml = mergeRuns(await zip.file(part).async("string"));
    for (const [key, value] of Object.entries(values)) {
      xml = xml.split("{{" + key + "}}").join(asXml(value));
    }
    let m;
    FIELD.lastIndex = 0;
    while ((m = FIELD.exec(xml))) unfilled.add(m[1]);
    zip.file(part, xml);
  }

  const blob = await zip.generateAsync({
    type: "blob",
    mimeType:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });

  // Download without a server. The file is assembled in the tab and never
  // leaves the machine.
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "document.docx";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);

  return Array.from(unfilled).sort();
}

/** Map Core's data onto the letter template's fields. */
export function letterValues({ entity, letterhead, recipient, subject, body, date }) {
  const lines = [
    recipient?.name, recipient?.line1, recipient?.line2,
    recipient?.city, recipient?.postcode, recipient?.country,
  ].filter(Boolean);

  return {
    REFERENCE: entity?.ref || "",
    DATE: (date || new Date()).toLocaleDateString("en-GB",
      { day: "numeric", month: "long", year: "numeric" }),
    ADDRESS: lines.join("\n"),
    SALUTATION: recipient?.salutation || "Sirs",
    SUBJECT: subject || "",
    BODY: body || "",
    // From letter_context(): the letterhead of whichever Affinity company
    // administers this entity, built from that company's own record.
    COMPANY_NAME: letterhead?.company_name || "",
    COMPANY_ADDRESS: letterhead?.address_line || "",
    COMPANY_FOOTER: letterhead?.footer_text || "",
    SIGNING_AS: letterhead?.signing_as || letterhead?.company_name || "",
  };
}
